import styled, { CSSObject } from "styled-components";
import Box from "../../layout/boxes/Box/Box";
import Label from "../Label/Label";
import { palette } from "../../../theming/bca/bca-palette";

import {
	StyledFieldsetProps,
	StyledLegendProps,
	StyledLabelProps,
	StyledMessageProps,
} from "./TextInputContainer.types";
import Typography from "../../typography/Typography";
import { Unit } from "../../../types/globals";

const TRANSITION_DURATION = 200;
const TRANSITION_TIMING_FUNCTION = "ease-in-out";

export const BOX_HEIGHT: Unit = "76px"; // TODO: Needs calculating
export const INPUT_HEIGHT: Unit = "64px";
export const INPUT_BORDER_SIZE: Unit = "1px";
export const INPUT_LABEL_SIZE = "md";
export const INPUT_LABEL_SIZE_FOCUSED = "sm";
export const INPUT_PADDING_X = "md";
export const LABEL_PADDING_X = "xxs";

export const StyledContainer = styled(Box)`
	position: relative;
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.space["xs"]};
`;

export const StyledFieldset = styled.fieldset<StyledFieldsetProps>`
	position: absolute;
	top: 0;
	left: 0;
	margin: 0;
	width: 100%;
	min-width: auto;
	pointer-events: none;
	padding: ${({ theme }) => `0 ${theme.space.md}`};
	height: 100%;
	border-radius: ${({ theme }) => theme.radii.sm};
	box-sizing: border-box;
	transition: border-color ${TRANSITION_DURATION}ms
		${TRANSITION_TIMING_FUNCTION};

	${(props) => {
		const COLOR_BG_LIGHT_VARIANT = palette.hexFor("bg_light_variant");

		const COLOR_NEUTRAL_600 = palette.hexFor("neutral_600");
		const COLOR_NEUTRAL_500 = palette.hexFor("neutral_500");
		const COLOR_NEUTRAL_400 = palette.hexFor("neutral_400");

		const COLOR_CTA_DEFAULT = palette.hexFor("cta_default");
		const COLOR_CTA_DISABLED = palette.hexFor("cta_disabled");

		const COLOR_SEMANTIC_ERROR_600 = palette.hexFor("semantic_error_600");
		const COLOR_SEMANTIC_ERROR_700 = palette.hexFor("semantic_error_700");
		const COLOR_SEMANTIC_SUCCESS_500 = palette.hexFor("semantic_success_500");
		const COLOR_SEMANTIC_SUCCESS_600 = palette.hexFor("semantic_success_500");

		const defaultOnDark: CSSObject = {
			border: `${INPUT_BORDER_SIZE} solid ${COLOR_BG_LIGHT_VARIANT}`,
			":disabled": {
				borderColor: COLOR_NEUTRAL_500,
				backgroundColor: COLOR_NEUTRAL_400,
			},
		};

		const errorOnDark: CSSObject = {
			border: `${INPUT_BORDER_SIZE} solid ${COLOR_SEMANTIC_ERROR_600}`,
			":disabled": {
				borderColor: COLOR_NEUTRAL_500,
				backgroundColor: COLOR_NEUTRAL_400,
			},
			":not(:disabled) + span": {
				color: COLOR_SEMANTIC_ERROR_600,
			},
		};

		const successOnDark: CSSObject = {
			border: `${INPUT_BORDER_SIZE} solid ${COLOR_SEMANTIC_SUCCESS_500}`,
			":disabled": {
				borderColor: COLOR_NEUTRAL_500,
				backgroundColor: COLOR_NEUTRAL_400,
			},
			":not(:disabled) + span": {
				color: COLOR_SEMANTIC_SUCCESS_500,
			},
		};

		const defaultOnLight: CSSObject = {
			border: `${INPUT_BORDER_SIZE} solid ${COLOR_NEUTRAL_600}`,
			":disabled": {
				borderColor: COLOR_NEUTRAL_600,
				backgroundColor: COLOR_CTA_DISABLED,
			},
		};

		const errorOnLight: CSSObject = {
			border: `${INPUT_BORDER_SIZE} solid ${COLOR_SEMANTIC_ERROR_600}`,
			":disabled": {
				borderColor: COLOR_NEUTRAL_600,
				backgroundColor: COLOR_CTA_DISABLED,
			},
			":not(:disabled) + span": {
				color: COLOR_SEMANTIC_ERROR_700,
			},
		};

		const successOnLight: CSSObject = {
			border: `${INPUT_BORDER_SIZE} solid ${COLOR_SEMANTIC_SUCCESS_500}`,
			":disabled": {
				borderColor: COLOR_NEUTRAL_600,
				backgroundColor: COLOR_CTA_DISABLED,
			},
			":not(:disabled) + span": {
				color: COLOR_SEMANTIC_SUCCESS_600,
			},
		};

		const focusedOnAny = {
			border: `${INPUT_BORDER_SIZE} solid ${COLOR_CTA_DEFAULT}`,
		};

		const styles = {
			default: {
				bg_dark: defaultOnDark,
				bg_dark_variant: defaultOnDark,
				bg_light: defaultOnLight,
				bg_light_variant: defaultOnLight,
				bg_white: defaultOnLight,
			},
			error: {
				bg_dark: errorOnDark,
				bg_dark_variant: errorOnDark,
				bg_light: errorOnLight,
				bg_light_variant: errorOnLight,
				bg_white: errorOnLight,
			},
			success: {
				bg_dark: successOnDark,
				bg_dark_variant: successOnDark,
				bg_light: successOnLight,
				bg_light_variant: successOnLight,
				bg_white: successOnLight,
			},
			focused: {
				bg_dark: focusedOnAny,
				bg_dark_variant: focusedOnAny,
				bg_light: focusedOnAny,
				bg_light_variant: focusedOnAny,
				bg_white: focusedOnAny,
			},
		};
		const state =
			props.focused && props.state === "default" ? "focused" : props.state;

		return styles[state][props.colorOn] || undefined;
	}}
`;

export const StyledLabel = styled(Label)<StyledLabelProps>`
	position: absolute;
	top: ${({ theme, focused }) =>
		focused ? theme.space[INPUT_LABEL_SIZE_FOCUSED] : theme.space.xl};
	left: calc(
		${({ theme }) => theme.space[INPUT_PADDING_X]} -
			${({ theme }) => theme.space[LABEL_PADDING_X]} + ${INPUT_BORDER_SIZE}
	);
	display: flex;
	align-items: center;
	width: fit-content;
	z-index: 2;
	transition: transform ${TRANSITION_DURATION}ms ${TRANSITION_TIMING_FUNCTION},
		font-size ${TRANSITION_DURATION}ms ${TRANSITION_TIMING_FUNCTION},
		color ${TRANSITION_DURATION}ms ${TRANSITION_TIMING_FUNCTION};
	pointer-events: none;
`;

export const StyledLegend = styled(Label)<StyledLegendProps>`
	display: block;
	margin-left: -${({ theme }) => theme.space[LABEL_PADDING_X]};
	visibility: hidden;
	white-space: nowrap;
	${({ focused, hasValue }) => {
		return {
			transition: focused
				? `max-width ${TRANSITION_DURATION / 2}ms cubic-bezier(0, 0, 0.2, 1) ${
						TRANSITION_DURATION / 4
				  }ms`
				: `max-width ${
						TRANSITION_DURATION / 4
				  }ms cubic-bezier(0, 0, 0.2, 1) 0ms`,
			maxWidth: focused || hasValue ? "100%" : "0.001%",
		};
	}}
`;

export const StyledMessage = styled(Typography)<StyledMessageProps>`
	${({ state, colorOn }) => {
		const COLOR_SEMANTIC_ERROR_600 = palette.hexFor("semantic_error_600");
		const COLOR_SEMANTIC_ERROR_700 = palette.hexFor("semantic_error_700");
		const COLOR_SEMANTIC_SUCCESS_500 = palette.hexFor("semantic_success_500");
		const COLOR_SEMANTIC_SUCCESS_600 = palette.hexFor("semantic_success_500");

		const errorOnDark: CSSObject = {
			color: COLOR_SEMANTIC_ERROR_600,
		};

		const successOnDark: CSSObject = {
			color: COLOR_SEMANTIC_SUCCESS_500,
		};

		const errorOnLight: CSSObject = {
			color: COLOR_SEMANTIC_ERROR_700,
		};

		const successOnLight: CSSObject = {
			color: COLOR_SEMANTIC_SUCCESS_600,
		};

		const styles = {
			error: {
				bg_dark: errorOnDark,
				bg_dark_variant: errorOnDark,
				bg_light: errorOnLight,
				bg_light_variant: errorOnLight,
				bg_white: errorOnLight,
			},
			success: {
				bg_dark: successOnDark,
				bg_dark_variant: successOnDark,
				bg_light: successOnLight,
				bg_light_variant: successOnLight,
				bg_white: successOnLight,
			},
		};

		return colorOn && (state === "error" || state === "success")
			? styles[state][colorOn]
			: undefined;
	}}
`;
